import React, { useState, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { useTranslation } from 'react-i18next';
interface InstagramCarouselPreviewProps {
    urls: string[];
    /** Optional proxy function for URLs that need re-writing (e.g. MinIO behind a proxy) */
    getProxyUrl?: (url: string) => string;
    posterUrl?: string | null;
}

export const InstagramCarouselPreview: React.FC<InstagramCarouselPreviewProps> = ({
    urls,
    getProxyUrl,
    posterUrl,
}) => {
    const { t } = useTranslation();
    const [activeIndex, setActiveIndex] = useState(0);
    const scrollRef = useRef<HTMLDivElement>(null);

    const scrollTo = useCallback((idx: number) => {
        if (!scrollRef.current) return;
        const clamped = Math.max(0, Math.min(idx, urls.length - 1));
        setActiveIndex(clamped);
        scrollRef.current.scrollTo({ left: clamped * scrollRef.current.clientWidth, behavior: 'smooth' });
    }, [urls.length]);

    const handleScroll = useCallback(() => {
        if (!scrollRef.current) return;
        const idx = Math.round(scrollRef.current.scrollLeft / (scrollRef.current.clientWidth || 1));
        setActiveIndex(Math.max(0, Math.min(idx, urls.length - 1)));
    }, [urls.length]);

    const resolveUrl = (url: string) => {
        if (url.startsWith('blob:')) return url; // blob: URLs can't be proxied
        if (getProxyUrl) return getProxyUrl(url); // Always proxy storage URLs
        return url;
    };

    const isVideo = (url: string) => /\.(mp4|mov|avi|webm)$/i.test(url);

    if (urls.length === 0) return null;

    return (
        <div className="relative w-full h-full flex flex-col select-none">
            {/* ── Scroll Strip ── */}
            <div
                ref={scrollRef}
                className="flex-1 flex overflow-x-auto snap-x snap-mandatory"
                style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' } as React.CSSProperties}
                onScroll={handleScroll}
            >
                {urls.map((url, idx) => (
                    <div
                        key={idx}
                        className="min-w-full h-full flex-shrink-0 snap-start flex items-center justify-center bg-black"
                    >
                        {isVideo(url) ? (
                            <video
                                src={resolveUrl(url)}
                                className="w-full h-full object-cover"
                                controls
                                playsInline
                                preload="metadata"
                                poster={posterUrl || undefined}
                            />
                        ) : (
                            <img
                                src={resolveUrl(url)}
                                alt={`Slide ${idx + 1}`}
                                className="w-full h-full object-cover"
                                draggable={false}
                            />
                        )}
                    </div>
                ))}
            </div>

            {/* ── Left / Right nav ── */}
            {urls.length > 1 && (
                <>
                    {activeIndex > 0 && (
                        <button
                            className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1 transition-colors z-10"
                            onClick={() => scrollTo(activeIndex - 1)}
                            aria-label={t('common.previous')}
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </button>
                    )}
                    {activeIndex < urls.length - 1 && (
                        <button
                            className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1 transition-colors z-10"
                            onClick={() => scrollTo(activeIndex + 1)}
                            aria-label={t('common.next')}
                        >
                            <ChevronRight className="w-4 h-4" />
                        </button>
                    )}
                </>
            )}

            {/* ── Dot indicators (normal flow, not absolute → never clipped) ── */}
            {urls.length > 1 && (
                <div className="flex justify-center gap-1.5 py-2 bg-black/30">
                    {urls.map((_, i) => (
                        <button
                            key={i}
                            onClick={() => scrollTo(i)}
                            aria-label={`Go to slide ${i + 1}`}
                            className="p-0 border-0 bg-transparent cursor-pointer"
                        >
                            <span
                                className="block rounded-full bg-white transition-all duration-200"
                                style={{
                                    width: i === activeIndex ? '8px' : '6px',
                                    height: i === activeIndex ? '8px' : '6px',
                                    opacity: i === activeIndex ? 1 : 0.45,
                                }}
                            />
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default InstagramCarouselPreview;
